// File: noviopus-ats-client/src/App/store.ts
import { configureStore as configureToolkitStore, getDefaultMiddleware, Store, AnyAction } from '@reduxjs/toolkit';
import createSagaMiddleware, { Task } from 'redux-saga';
import { createBrowserHistory } from 'history';
import { persistStore, persistReducer, Persistor } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { createLogger } from 'redux-logger';

import createReducer from './reducers';
import rootSaga from '@/App/sagas'; 
import { routerMiddleware } from '@/middleware/routerMiddleware'; 

export const history = createBrowserHistory();

const isProduction = process.env.NODE_ENV === 'production';

const persistConfig = {
  key: 'root',
  storage,
  whitelist: ['auth'],
};

const sagaMiddleware = createSagaMiddleware();

// FIX: Updated Interface to support dynamic Sagas
export interface ExtendedStore extends Store<any, AnyAction> {
  runSaga: (saga: any) => Task;
  injectedReducers: Record<string, any>;
  injectedSagas: Record<string, boolean>; // Registry for sagas
  injectReducer: (key: string, asyncReducer: any) => void;
  injectSaga?: (key: string, saga: any) => void; // Optional compatibility method
}

export let store: ExtendedStore;
export let persistor: Persistor;

export default function configureStore(initialState = {}, historyArg = history) {
  // We handle the root reducer creation here
  // Note: createReducer now only accepts the asyncReducers object
  const rootReducer = createReducer(); 
  
  const persistedReducer = persistReducer(persistConfig, rootReducer);

  const createdStore = configureToolkitStore({
    reducer: persistedReducer,
    middleware: [
      ...getDefaultMiddleware({
        serializableCheck: false,
        thunk: false,
      }),
      sagaMiddleware,
      routerMiddleware(historyArg),
      ...(!isProduction ? [createLogger({ collapsed: true })] : []),
    ],
    devTools: !isProduction,
    preloadedState: initialState,
  }) as unknown as ExtendedStore; // Cast to ExtendedStore

  // Attach Saga Middleware Run
  createdStore.runSaga = sagaMiddleware.run;

  // Initialize Injection Registries
  createdStore.injectedReducers = {};
  createdStore.injectedSagas = {}; // FIX: Initialize this

  // Define Reducer Injection Logic
  createdStore.injectReducer = (key: string, asyncReducer: any) => {
    // Only inject if not already present
    if (createdStore.injectedReducers[key]) return;

    createdStore.injectedReducers[key] = asyncReducer;
    
    // Replace reducer with new combined reducer including async ones
    createdStore.replaceReducer(
      persistReducer(persistConfig, createReducer(createdStore.injectedReducers))
    );
  };

  // Optional: Define Helper for Saga Injection (Matches Career App pattern if needed)
  createdStore.injectSaga = (key: string, saga: any) => {
    if (createdStore.injectedSagas[key]) return;
    
    createdStore.runSaga(saga);
    createdStore.injectedSagas[key] = true;
  };

  store = createdStore;
  
  // Run Root Saga
  sagaMiddleware.run(rootSaga);
  
  persistor = persistStore(store);

  return { store, persistor };
}